import React from 'react';
import { Typography, Select } from 'antd';

interface EndTimeOption {
  label: string;
  value: string;
}

interface StartEndTimeSelectorProps {
  timeOptions: EndTimeOption[];
  selectedTime: Date;
  labelText: string;
  onChange: (value: string) => void;
  // When true, render only the full-width Select (no inline "label:" prefix), so
  // a caller can place it inside its own vertical/labelled form layout.
  controlOnly?: boolean;
}

const StartEndTimeSelector: React.FC<StartEndTimeSelectorProps> = ({
  timeOptions,
  selectedTime,
  labelText,
  onChange,
  controlOnly = false,
}) => {
  // Resolve which option is selected by comparing the underlying *instant*
  // rather than the raw ISO string. The option values and `selectedTime` are
  // built independently (option values via setHours(...).toISOString(); the
  // booking's end via date arithmetic), so a byte-for-byte string match is
  // fragile — a millisecond or offset-representation difference would leave the
  // Select with no highlighted option and show the raw ISO text. Matching on
  // getTime() is exact for the same instant and robust to formatting.
  const selectedMs =
    selectedTime instanceof Date && !Number.isNaN(selectedTime.getTime())
      ? selectedTime.getTime()
      : NaN;

  const matchedOption = Number.isNaN(selectedMs)
    ? undefined
    : timeOptions.find((o) => {
        const optionMs = new Date(o.value).getTime();
        return !Number.isNaN(optionMs) && optionMs === selectedMs;
      });

  // Only ever hand AntD a value that exists in `options` (so the highlight is
  // correct); otherwise leave it unset and show the placeholder rather than a
  // dangling, non-matching value. (The selected end time itself still lives in
  // the parent's state — snapping the default onto a valid option is the
  // parent's job; this component just renders the selection truthfully.)
  const value = matchedOption?.value;

  const select = (
    <Select
      style={controlOnly ? { width: '100%' } : { minWidth: '150px', textAlign: 'right' }}
      placeholder="Select time"
      options={timeOptions}
      onChange={onChange}
      value={value}
    />
  );

  if (controlOnly) {
    return select;
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
      <Typography.Text>
        <strong>{labelText}</strong>
      </Typography.Text>
      {select}
    </div>
  );
};

export default StartEndTimeSelector;
